(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function goldRgba(alpha) {
    var rgb =
      getComputedStyle(document.documentElement).getPropertyValue('--plz-color-gold-rgb').trim() ||
      '184, 134, 11';
    return 'rgba(' + rgb + ',' + alpha + ')';
  }

  function parseMilestones(root) {
    var map = {};
    var script = root.querySelector('[data-journey-milestones]');
    if (!script) return map;

    try {
      var items = JSON.parse(script.textContent);
      items.forEach(function (item) {
        if (item && item.day && item.tip) {
          map[item.day] = item.tip;
        }
      });
    } catch (e) {
      /* ignore invalid JSON */
    }

    return map;
  }

  function runCanvasLoop(canvas, drawFrame) {
    if (reducedMotion) {
      drawFrame(0, true);
      return function () {};
    }

    var rafId = null;
    var visible = true;
    var observer = new IntersectionObserver(
      function (entries) {
        visible = entries[0].isIntersecting;
        if (visible && rafId === null) {
          rafId = requestAnimationFrame(tick);
        }
      },
      { threshold: 0.08 }
    );

    observer.observe(canvas);

    function tick() {
      if (!visible) {
        rafId = null;
        return;
      }
      drawFrame();
      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);

    return function () {
      observer.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }

  function initNoiseWave(canvas) {
    var ctx = canvas.getContext('2d');
    var phase = 0;
    var isHovered = false;
    var w = 0;
    var h = 0;
    var wrap = canvas.parentElement;
    var touchLike = window.matchMedia('(hover: none), (pointer: coarse)').matches;

    function resizeCanvas() {
      var cssWidth = wrap ? wrap.clientWidth : canvas.clientWidth;
      var cssHeight = parseFloat(window.getComputedStyle(canvas).height) || 100;
      var dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      w = cssWidth;
      h = cssHeight;
    }

    function setCalm(calm) {
      isHovered = calm;
      if (touchLike && wrap) {
        wrap.setAttribute('aria-pressed', calm ? 'true' : 'false');
      }
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    if (touchLike && wrap) {
      wrap.setAttribute('role', 'button');
      wrap.setAttribute('tabindex', '0');
      wrap.setAttribute('aria-pressed', 'false');

      wrap.addEventListener('click', function () {
        setCalm(!isHovered);
      });

      wrap.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setCalm(!isHovered);
        }
      });
    } else {
      canvas.addEventListener('mouseenter', function () {
        setCalm(true);
      });
      canvas.addEventListener('mouseleave', function () {
        setCalm(false);
      });
    }

    runCanvasLoop(canvas, function () {
      ctx.clearRect(0, 0, w, h);
      for (var l = 0; l < 5; l++) {
        var po = l * Math.PI * 0.45;
        var amp = isHovered ? 0 : 22 * (1 - l * 0.16);
        var alpha = Math.max(0.85 - l * 0.15, 0.1);
        ctx.beginPath();
        ctx.strokeStyle = goldRgba(alpha);
        ctx.lineWidth = Math.max(2.4 - l * 0.35, 0.7);
        ctx.lineJoin = 'round';
        for (var x = 0; x <= w; x++) {
          var n = x / w;
          var y =
            h / 2 +
            Math.sin(n * Math.PI * 2 * 4.5 + phase + po) * amp +
            Math.sin(n * Math.PI * 2 * 7.2 + phase * 1.4 + po) * amp * 0.32 +
            Math.sin(n * Math.PI * 2 * 2.1 + phase * 0.7 + po) * amp * 0.18;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      if (!reducedMotion) phase += 0.055;
    });
  }

  function resizeJourneyCanvas(canvas) {
    var cssWidth = canvas.clientWidth || 400;
    var cssHeight = parseFloat(window.getComputedStyle(canvas).height) || 52;
    var dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);
    var ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx: ctx, w: cssWidth, h: cssHeight };
  }

  var journeyMobileMq = window.matchMedia('(max-width: 900px)');
  var JOURNEY_TIP_MARGIN = 12;

  function isJourneyMobile() {
    return journeyMobileMq.matches;
  }

  function resetJourneyTipStyles(tip) {
    if (!tip) return;
    tip.style.removeProperty('--plz-journey-tip-x');
    tip.style.removeProperty('--plz-journey-tip-y');
    tip.style.removeProperty('--plz-journey-tip-arrow-offset');
    tip.style.removeProperty('--plz-journey-tip-shift');
  }

  function positionJourneyTip(cell) {
    var tip = cell.querySelector('.plz-cmh-journey__tip');
    if (!tip) return;

    var open = cell.classList.contains('is-active');
    var hovered = cell.matches(':hover');

    if (!open && !hovered) {
      resetJourneyTipStyles(tip);
      return;
    }

    if (isJourneyMobile() && open) {
      var cellRect = cell.getBoundingClientRect();
      var viewportWidth = document.documentElement.clientWidth;
      var tipWidth = Math.min(tip.getBoundingClientRect().width || 256, viewportWidth - JOURNEY_TIP_MARGIN * 2);
      var cellCenterX = cellRect.left + cellRect.width / 2;
      var tipCenterX = Math.max(
        JOURNEY_TIP_MARGIN + tipWidth / 2,
        Math.min(cellCenterX, viewportWidth - JOURNEY_TIP_MARGIN - tipWidth / 2)
      );

      tip.style.setProperty('--plz-journey-tip-x', tipCenterX + 'px');
      tip.style.setProperty('--plz-journey-tip-y', cellRect.bottom + 8 + 'px');
      tip.style.setProperty('--plz-journey-tip-arrow-offset', cellCenterX - tipCenterX + 'px');
      tip.style.removeProperty('--plz-journey-tip-shift');
      return;
    }

    tip.style.removeProperty('--plz-journey-tip-x');
    tip.style.removeProperty('--plz-journey-tip-y');
    tip.style.removeProperty('--plz-journey-tip-arrow-offset');
    tip.style.setProperty('--plz-journey-tip-shift', '0px');

    var rect = tip.getBoundingClientRect();
    var viewportWidth = document.documentElement.clientWidth;
    var shift = 0;

    if (rect.right > viewportWidth - JOURNEY_TIP_MARGIN) {
      shift = viewportWidth - JOURNEY_TIP_MARGIN - rect.right;
    } else if (rect.left < JOURNEY_TIP_MARGIN) {
      shift = JOURNEY_TIP_MARGIN - rect.left;
    }

    tip.style.setProperty('--plz-journey-tip-shift', Math.round(shift) + 'px');
  }

  function scheduleJourneyTipPosition(cell) {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        positionJourneyTip(cell);
      });
    });
  }

  function initJourney(root) {
    if (root.dataset.plzJourneyInit === 'true') return;
    root.dataset.plzJourneyInit = 'true';

    var touchLike = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    var useMobileTipBar = touchLike || isJourneyMobile();
    var mobileTipBar = null;

    if (useMobileTipBar) {
      mobileTipBar = document.createElement('div');
      mobileTipBar.className = 'plz-cmh-journey__mobile-tip';
      mobileTipBar.setAttribute('role', 'status');
      mobileTipBar.setAttribute('aria-live', 'polite');
      mobileTipBar.hidden = true;

      var monthsSlider = root.querySelector('.plz-cmh-journey__months-slider');
      var monthDots = monthsSlider && monthsSlider.querySelector('.plz-cmh-journey__months-dots');
      if (monthDots) {
        monthDots.insertAdjacentElement('afterend', mobileTipBar);
      } else if (monthsSlider) {
        monthsSlider.appendChild(mobileTipBar);
      }
    }

    function setMobileTip(text, visible) {
      if (!mobileTipBar) return;
      if (visible && text) {
        mobileTipBar.textContent = text;
        mobileTipBar.hidden = false;
        mobileTipBar.classList.add('is-visible');
      } else {
        mobileTipBar.hidden = true;
        mobileTipBar.classList.remove('is-visible');
        mobileTipBar.textContent = '';
      }
    }

    function closeAllTips() {
      root.querySelectorAll('.plz-cmh-journey__cell--ms.is-active').forEach(function (cell) {
        cell.classList.remove('is-active');
        resetJourneyTipStyles(cell.querySelector('.plz-cmh-journey__tip'));
      });
      setMobileTip('', false);
    }

    root.addEventListener('click', function (e) {
      if (e.target.closest('.plz-cmh-journey__cell--ms')) return;
      closeAllTips();
    });

    var monthsViewport = root.querySelector('.plz-cmh-journey__months-viewport');
    if (monthsViewport) {
      monthsViewport.addEventListener('pointerdown', function (e) {
        if (e.target.closest('.plz-cmh-journey__cell--ms')) return;
        closeAllTips();
      });
    }

    var ms = parseMilestones(root);
    var cfgs = [
      { amp: 26, freq: 4.2, speed: 0.048, lines: 4, alpha: 0.85, lw: 2.2 },
      { amp: 13, freq: 2.8, speed: 0.024, lines: 2, alpha: 0.5, lw: 1.6 },
      { amp: 4, freq: 1.4, speed: 0.008, lines: 1, alpha: 0.22, lw: 1.1 }
    ];
    var canvases = root.querySelectorAll('[data-journey-wave]');
    var phases = [0, 0, 0];
    var canvasStates = [];

    canvases.forEach(function (canvas, m) {
      canvasStates[m] = resizeJourneyCanvas(canvas);

      var grid = root.querySelector('[data-journey-grid="' + m + '"]');
      if (!grid) return;

      for (var d = 1; d <= 30; d++) {
        var dn = m * 30 + d;
        var cell = document.createElement('div');
        cell.className = 'plz-cmh-journey__cell';

        if (ms[dn]) {
          cell.classList.add('plz-cmh-journey__cell--ms');
          cell.textContent = d;
          cell.setAttribute('tabindex', '0');
          cell.setAttribute('role', 'button');
          cell.setAttribute('aria-label', ms[dn]);

          var tip = document.createElement('div');
          tip.className = 'plz-cmh-journey__tip';
          tip.textContent = ms[dn];
          tip.setAttribute('aria-hidden', 'true');
          cell.appendChild(tip);

          (function (activeCell, tipText) {
            function toggleTip(active) {
              activeCell.classList.toggle('is-active', active);
              var tip = activeCell.querySelector('.plz-cmh-journey__tip');

              if (useMobileTipBar) {
                if (active) {
                  root.querySelectorAll('.plz-cmh-journey__cell--ms.is-active').forEach(function (other) {
                    if (other !== activeCell) other.classList.remove('is-active');
                  });
                  setMobileTip(tipText, true);
                } else {
                  setMobileTip('', false);
                }
                return;
              }

              if (active) {
                scheduleJourneyTipPosition(activeCell);
              } else {
                resetJourneyTipStyles(tip);
              }
            }

            function closeTip() {
              toggleTip(false);
            }

            activeCell.addEventListener('pointerdown', function (e) {
              e.stopPropagation();
            });

            activeCell.addEventListener('click', function (e) {
              e.stopPropagation();
              e.preventDefault();
              var open = activeCell.classList.contains('is-active');
              root.querySelectorAll('.plz-cmh-journey__cell--ms.is-active').forEach(function (other) {
                if (other !== activeCell) {
                  other.classList.remove('is-active');
                  resetJourneyTipStyles(other.querySelector('.plz-cmh-journey__tip'));
                }
              });
              if (!open) toggleTip(true);
              else closeTip();
            });

            activeCell.addEventListener('keydown', function (e) {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                var open = activeCell.classList.contains('is-active');
                root.querySelectorAll('.plz-cmh-journey__cell--ms.is-active').forEach(function (other) {
                  if (other !== activeCell) {
                    other.classList.remove('is-active');
                    resetJourneyTipStyles(other.querySelector('.plz-cmh-journey__tip'));
                  }
                });
                if (!open) toggleTip(true);
                else closeTip();
              }
              if (e.key === 'Escape') closeTip();
            });

            if (!touchLike) {
              activeCell.addEventListener('blur', closeTip);

              activeCell.addEventListener('pointerenter', function () {
                scheduleJourneyTipPosition(activeCell);
              });

              activeCell.addEventListener('pointerleave', function () {
                if (!activeCell.classList.contains('is-active')) {
                  resetJourneyTipStyles(activeCell.querySelector('.plz-cmh-journey__tip'));
                }
              });
            }
          })(cell, ms[dn]);
        } else {
          cell.classList.add('plz-cmh-journey__cell--filled');
          cell.textContent = d;
          if (m === 1) cell.style.opacity = '0.6';
          if (m === 2) cell.style.opacity = '0.35';
        }

        grid.appendChild(cell);
      }
    });

    function onResize() {
      canvases.forEach(function (canvas, m) {
        canvasStates[m] = resizeJourneyCanvas(canvas);
      });
      root.querySelectorAll('.plz-cmh-journey__cell--ms.is-active').forEach(function (cell) {
        scheduleJourneyTipPosition(cell);
      });
    }

    window.addEventListener('resize', onResize);

    document.addEventListener('shopify:section:unload', function onSectionUnload(event) {
      if (!event.target.contains(root)) return;
      window.removeEventListener('resize', onResize);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
    });

    if (canvases.length) {
      runCanvasLoop(canvases[0], function () {
        canvases.forEach(function (canvas, m) {
          var cfg = cfgs[m];
          var state = canvasStates[m];
          if (!state) return;

          if (!reducedMotion) phases[m] += cfg.speed;

          var ctx = state.ctx;
          var w = state.w;
          var h = state.h;
          ctx.clearRect(0, 0, w, h);
          for (var l = 0; l < cfg.lines; l++) {
            var po = l * Math.PI * 0.5;
            var la = cfg.amp * (1 - l * 0.22);
            var alpha = cfg.alpha * (1 - l * 0.2);
            ctx.beginPath();
            ctx.strokeStyle = goldRgba(Math.max(alpha, 0.06));
            ctx.lineWidth = Math.max(cfg.lw - l * 0.3, 0.7);
            ctx.lineJoin = 'round';
            for (var x = 0; x <= w; x++) {
              var n = x / w;
              var y =
                h / 2 +
                Math.sin(n * Math.PI * 2 * cfg.freq + phases[m] + po) * la +
                Math.sin(n * Math.PI * 2 * (cfg.freq * 1.6) + phases[m] * 1.3 + po) * la * 0.3;
              if (x === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
            }
            ctx.stroke();
          }
        });
      });
    }
  }

  function initFormula(root) {
    if (root.dataset.plzFormulaInit === 'true') return;
    root.dataset.plzFormulaInit = 'true';

    var touchLike = window.matchMedia('(hover: none), (pointer: coarse)').matches;

    root.addEventListener('click', function (e) {
      var card = e.target.closest('.plz-cmh-formula__card');
      if (!card || !root.contains(card)) return;

      var flipped = card.classList.toggle('is-flipped');
      card.setAttribute('aria-expanded', flipped ? 'true' : 'false');

      if (touchLike) {
        card.blur();
      }
    });
  }

  function createHeroNavButton(direction) {
    var btn = document.createElement('button');
    var isPrev = direction === 'prev';
    var heroRoot = document.querySelector('[data-plz-cmh-hero-slider]');
    var prevLabel =
      (heroRoot && heroRoot.getAttribute('data-i18n-prev-image')) || 'Previous image';
    var nextLabel =
      (heroRoot && heroRoot.getAttribute('data-i18n-next-image')) || 'Next image';
    btn.type = 'button';
    btn.className =
      'plz-cmh-hero__slider-nav plz-cmh-hero__slider-nav--' + (isPrev ? 'prev' : 'next');
    btn.setAttribute('data-plz-cmh-slider-' + direction, '');
    btn.setAttribute('aria-label', isPrev ? prevLabel : nextLabel);
    btn.innerHTML =
      '<span class="svg-wrapper" aria-hidden="true"><svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg></span>';
    return btn;
  }

  function hydrateHeroProductSlider() {
    var mqMobile = window.matchMedia('(max-width: 900px)').matches;
    var touchLike = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    if (!mqMobile && !touchLike) return;

    var heroSlider = document.querySelector('[data-plz-cmh-hero-slider]');
    if (!heroSlider || heroSlider.dataset.plzHeroHydrated === 'true') return;
    if (heroSlider.hasAttribute('data-plz-cmh-hero-slider-blocks')) return;

    var track = heroSlider.querySelector('.plz-cmh-slider__track');
    var gallerySection = document.querySelector('.plz-cmh-gallery-section');
    if (!track || !gallerySection) return;

    var gallerySlides = gallerySection.querySelectorAll(
      '[data-plz-shop-gallery-slide]:not(.plz-shop-gallery__slide--clone)'
    );
    if (!gallerySlides.length) return;

    var existingSrcs = {};
    Array.prototype.forEach.call(track.querySelectorAll('img'), function (img) {
      var src = img.currentSrc || img.src;
      if (src) existingSrcs[src] = true;
    });

    gallerySlides.forEach(function (gallerySlide) {
      var sourceImg = gallerySlide.querySelector('img');
      if (!sourceImg) return;

      var src = sourceImg.currentSrc || sourceImg.src;
      if (!src || existingSrcs[src]) return;
      existingSrcs[src] = true;

      var slide = document.createElement('div');
      slide.className = 'plz-cmh-hero__slide';

      var img = document.createElement('img');
      img.className = 'plz-cmh-hero__image';
      img.src = src;
      img.alt = sourceImg.alt || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      if (sourceImg.srcset) img.srcset = sourceImg.srcset;
      if (sourceImg.sizes) img.sizes = sourceImg.sizes;

      slide.appendChild(img);
      track.appendChild(slide);
    });

    if (track.children.length < 2) return;

    heroSlider.dataset.plzHeroHydrated = 'true';

    var prevBtn = heroSlider.querySelector('[data-plz-cmh-slider-prev]');
    var nextBtn = heroSlider.querySelector('[data-plz-cmh-slider-next]');

    if (!prevBtn) {
      prevBtn = createHeroNavButton('prev');
      heroSlider.appendChild(prevBtn);
    }
    if (!nextBtn) {
      nextBtn = createHeroNavButton('next');
      heroSlider.appendChild(nextBtn);
    }

    prevBtn.hidden = false;
    nextBtn.hidden = false;
  }

  function initPlzCmhSlider(slider) {
    if (slider.dataset.plzCmhSliderReady === 'true') return;

    var viewport = slider.querySelector('.plz-cmh-slider__viewport');
    var track = slider.querySelector('.plz-cmh-slider__track');
    var slides = track ? Array.prototype.slice.call(track.children) : [];
    if (!viewport || !track || slides.length < 2) return;

    slider.dataset.plzCmhSliderReady = 'true';

    var alwaysCarousel = slider.hasAttribute('data-plz-cmh-slider-always');
    var stepBySlide = slider.hasAttribute('data-plz-cmh-slider-step-slide');
    var perPageAttr = parseInt(slider.getAttribute('data-plz-cmh-slider-per-page'), 10) || 0;
    var mq = window.matchMedia('(max-width: 900px)');
    var desktopMq = window.matchMedia('(min-width: 901px)');
    var dots = Array.prototype.slice.call(slider.querySelectorAll('[data-plz-cmh-slider-dot]'));
    var prevBtn = slider.querySelector('[data-plz-cmh-slider-prev]');
    var nextBtn = slider.querySelector('[data-plz-cmh-slider-next]');
    var autoplay = slider.getAttribute('data-plz-cmh-slider-autoplay') !== 'false';
    var index = 0;
    var timer = null;
    var interval = 6000;
    var visible = true;
    var dragging = false;
    var startX = 0;
    var currentX = 0;
    var activePointer = null;

    function isSliderActive() {
      var touchLike = window.matchMedia('(hover: none), (pointer: coarse)').matches;
      return alwaysCarousel || mq.matches || touchLike;
    }

    function getSlidesPerPage() {
      if (perPageAttr > 0 && desktopMq.matches) {
        return Math.min(perPageAttr, slides.length);
      }
      return 1;
    }

    function getPageCount() {
      return Math.max(1, Math.ceil(slides.length / getSlidesPerPage()));
    }

    function getSlideGap() {
      var styles = window.getComputedStyle(track);
      return parseFloat(styles.columnGap || styles.gap) || 0;
    }

    function getSlideStep() {
      var slidesPerPage = getSlidesPerPage();
      if (slidesPerPage > 1) {
        return viewport.offsetWidth + getSlideGap();
      }
      if (stepBySlide && slides[0]) {
        return slides[0].offsetWidth + getSlideGap();
      }
      return (viewport.offsetWidth || 1) + getSlideGap();
    }

    function setTranslate(offsetPx, animate) {
      track.style.transition = animate === false ? 'none' : '';
      track.style.transform = 'translate3d(-' + offsetPx + 'px, 0, 0)';
    }

    function updateDots() {
      var slidesPerPage = getSlidesPerPage();
      dots.forEach(function (dot, dotIndex) {
        if (slidesPerPage > 1) {
          var pageIndex = Math.floor(dotIndex / slidesPerPage);
          var show = dotIndex % slidesPerPage === 0;
          dot.hidden = !show;
          var active = pageIndex === index;
          dot.classList.toggle('is-active', active);
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
          return;
        }

        dot.hidden = false;
        var activeSlide = dotIndex === index;
        dot.classList.toggle('is-active', activeSlide);
        if (activeSlide) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    function goTo(nextIndex) {
      var pageCount = getPageCount();
      var slidesPerPage = getSlidesPerPage();
      if (slidesPerPage > 1) {
        index = ((nextIndex % pageCount) + pageCount) % pageCount;
      } else {
        index = (nextIndex + slides.length) % slides.length;
      }
      if (!isSliderActive()) return;
      setTranslate(index * getSlideStep(), true);
      updateDots();
      slider.dispatchEvent(new CustomEvent('plz-cmh-slider-change', { bubbles: true }));
    }

    function resetDesktopLayout() {
      stop();
      dragging = false;
      activePointer = null;
      viewport.classList.remove('is-dragging');
      if (alwaysCarousel) {
        goTo(index);
        return;
      }
      track.style.transform = '';
      track.style.transition = '';
      index = 0;
      updateDots();
    }

    function applyDragOffset(deltaPx) {
      setTranslate(index * getSlideStep() - deltaPx, false);
    }

    function stop() {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    }

    function start() {
      stop();
      if (!autoplay || !isSliderActive() || reducedMotion || slides.length < 2 || !visible || dragging) return;
      timer = setInterval(function () {
        goTo(index + 1);
      }, interval);
    }

    function syncMode() {
      if (isSliderActive()) {
        if (index >= getPageCount()) index = 0;
        goTo(index);
        start();
      } else {
        resetDesktopLayout();
      }
    }

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      activePointer = null;
      viewport.classList.remove('is-dragging');
      track.style.transition = '';

      var delta = currentX - startX;
      var width = viewport.offsetWidth || 1;
      var threshold = Math.min(56, width * 0.14);

      if (delta < -threshold) {
        goTo(index + 1);
      } else if (delta > threshold) {
        goTo(index - 1);
      } else {
        goTo(index);
      }

      start();
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        if (!isSliderActive()) return;
        var dotIndex = parseInt(dot.getAttribute('data-plz-cmh-slider-dot'), 10);
        var slidesPerPage = getSlidesPerPage();
        if (slidesPerPage > 1) {
          goTo(Math.floor(dotIndex / slidesPerPage));
        } else {
          goTo(dotIndex);
        }
        start();
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        if (!isSliderActive()) return;
        goTo(index - 1);
        start();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!isSliderActive()) return;
        goTo(index + 1);
        start();
      });
    }

    viewport.addEventListener('pointerdown', function (e) {
      if (!isSliderActive()) return;
      if (e.target.closest('.plz-cmh-journey__cell--ms')) return;
      if (e.target.closest('[data-plz-cmh-slider-prev], [data-plz-cmh-slider-next], [data-plz-cmh-slider-dot]')) return;
      if (e.target.closest('a')) return;
      if (
        e.pointerType === 'mouse' &&
        e.target.closest('[data-plz-vtest-open], .plz-cmh-vtest__play')
      ) {
        return;
      }
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      activePointer = e.pointerId;
      startX = e.clientX;
      currentX = startX;
      stop();
      viewport.classList.add('is-dragging');
      track.style.transition = 'none';
      if (viewport.setPointerCapture) {
        viewport.setPointerCapture(e.pointerId);
      }
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== activePointer) return;
      currentX = e.clientX;
      applyDragOffset(currentX - startX);
    });

    viewport.addEventListener('pointerup', function (e) {
      if (e.pointerId !== activePointer) return;
      if (viewport.releasePointerCapture) {
        try {
          viewport.releasePointerCapture(e.pointerId);
        } catch (err) {
          /* ignore */
        }
      }
      endDrag();
    });

    viewport.addEventListener('pointercancel', function (e) {
      if (e.pointerId !== activePointer) return;
      endDrag();
    });

    slider.addEventListener('mouseenter', stop);
    slider.addEventListener('mouseleave', start);
    slider.addEventListener('focusin', stop);
    slider.addEventListener('focusout', start);

    var observer = new IntersectionObserver(
      function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) {
          start();
        } else {
          stop();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(slider);

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', syncMode);
      desktopMq.addEventListener('change', syncMode);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(syncMode);
      desktopMq.addListener(syncMode);
    }

    window.addEventListener('resize', syncMode);

    syncMode();
  }

  function initVideoTestimonials(root) {
    var openers = root.querySelectorAll('[data-plz-vtest-open]');
    if (!openers.length) return;

    var modal = null;
    var modalVideo = null;
    var modalAuthor = null;
    var lastFocus = null;

    function ensureModal() {
      if (modal) return modal;

      modal = document.createElement('div');
      modal.className = 'plz-cmh-vtest-modal';
      modal.hidden = true;
      modal.innerHTML =
        '<div class="plz-cmh-vtest-modal__backdrop" data-plz-vtest-close tabindex="-1"></div>' +
        '<div class="plz-cmh-vtest-modal__dialog" role="dialog" aria-modal="true" aria-label="' +
        (root.getAttribute('data-i18n-video-modal') || 'Video testimonial').replace(/"/g, '&quot;') +
        '">' +
        '<button type="button" class="plz-cmh-vtest-modal__close" data-plz-vtest-close aria-label="' +
        (root.getAttribute('data-i18n-close') || 'Close').replace(/"/g, '&quot;') +
        '">' +
        '<span aria-hidden="true">&times;</span></button>' +
        '<div class="plz-cmh-vtest-modal__media">' +
        '<video class="plz-cmh-vtest-modal__video" data-plz-vtest-modal-video playsinline controls controlslist="nodownload" disablepictureinpicture preload="metadata"></video>' +
        '</div>' +
        '<p class="plz-cmh-vtest-modal__author" data-plz-vtest-modal-author hidden></p>' +
        '</div>';

      document.body.appendChild(modal);

      modalVideo = modal.querySelector('[data-plz-vtest-modal-video]');
      modalAuthor = modal.querySelector('[data-plz-vtest-modal-author]');
      modalVideo.setAttribute('controlsList', 'nodownload');
      modalVideo.disablePictureInPicture = true;

      modal.querySelectorAll('[data-plz-vtest-close]').forEach(function (node) {
        node.addEventListener('click', closeModal);
      });

      if (!window.__plzVtestModalKeydown) {
        window.__plzVtestModalKeydown = true;
        document.addEventListener('keydown', function (event) {
          if (!modal || modal.hidden) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            closeModal();
          }
        });
      }

      return modal;
    }

    function closeModal() {
      if (!modal || modal.hidden) return;

      modal.classList.remove('is-opening');
      modal.hidden = true;
      document.body.classList.remove('plz-cmh-vtest-modal-open');

      if (modalVideo) {
        modalVideo.pause();
        modalVideo.removeAttribute('poster');
        modalVideo.removeAttribute('src');
        modalVideo.innerHTML = '';
      }

      if (modalAuthor) {
        modalAuthor.textContent = '';
        modalAuthor.hidden = true;
      }

      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus();
      }
    }

    function openModal(opener) {
      var src = opener.getAttribute('data-plz-vtest-src');
      if (!src) return;

      var mime = opener.getAttribute('data-plz-vtest-mime') || 'video/mp4';
      var poster = opener.getAttribute('data-plz-vtest-poster');
      var author = opener.getAttribute('data-plz-vtest-author');

      ensureModal();
      lastFocus = opener;

      modalVideo.pause();
      modalVideo.innerHTML = '';
      var source = document.createElement('source');
      source.src = src;
      source.type = mime;
      modalVideo.appendChild(source);

      if (poster) {
        modalVideo.setAttribute('poster', poster);
      } else {
        modalVideo.removeAttribute('poster');
      }

      modalVideo.load();

      if (author) {
        modalAuthor.textContent = author;
        modalAuthor.hidden = false;
      } else {
        modalAuthor.textContent = '';
        modalAuthor.hidden = true;
      }

      modal.hidden = false;
      document.body.classList.add('plz-cmh-vtest-modal-open');
      modal.classList.remove('is-opening');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          modal.classList.add('is-opening');
        });
      });
      modal.querySelector('.plz-cmh-vtest-modal__close').focus();

      var playPromise = modalVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          /* autoplay blocked until user interacts with controls */
        });
      }
    }

    openers.forEach(function (opener) {
      var pointerStartX = 0;
      var pointerStartY = 0;
      var pointerMoved = false;

      opener.addEventListener(
        'pointerdown',
        function (event) {
          pointerStartX = event.clientX;
          pointerStartY = event.clientY;
          pointerMoved = false;
        },
        { passive: true }
      );

      opener.addEventListener(
        'pointermove',
        function (event) {
          if (event.pointerType === 'mouse') return;
          if (
            Math.abs(event.clientX - pointerStartX) > 8 ||
            Math.abs(event.clientY - pointerStartY) > 8
          ) {
            pointerMoved = true;
          }
        },
        { passive: true }
      );

      opener.addEventListener('click', function (event) {
        if (pointerMoved && event.pointerType !== 'mouse') {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        openModal(opener);
      });
    });
  }

  function boot() {
    if (window.__plzCmhBooted) return;
    window.__plzCmhBooted = true;

    hydrateHeroProductSlider();
    document.querySelectorAll('[data-plz-cmh-noise-wave]').forEach(initNoiseWave);
    document.querySelectorAll('[data-plz-cmh-journey]').forEach(initJourney);
    document.querySelectorAll('[data-plz-cmh-formula]').forEach(initFormula);
    document.querySelectorAll('[data-plz-cmh-slider]').forEach(initPlzCmhSlider);
    document.querySelectorAll('[data-plz-cmh-vtest]').forEach(initVideoTestimonials);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
